import type { ReactNode } from 'react';
import Markdown from 'react-markdown';
import { motion } from 'motion/react';
import { eases } from '@/shared/constants/motion.constants';
export interface AgentIntroProps {
  icon: ReactNode;
  /** A tile behind the icon in the agent's category colour, for an icon drawn without one. */
  tagBackground?: string;
  title: string;
  description?: string;
  /** Markdown the agent opens a conversation with. */
  greeting?: string;
  /** Drawn under the greeting, for a host that lets it be edited. */
  children?: ReactNode;
  className?: string;
}

/** How an agent introduces itself in an empty conversation: who it is, then what it says first. */
export function AgentIntro({
  icon,
  tagBackground,
  title,
  description,
  greeting,
  children,
  className = 'h-[60vh]',
}: AgentIntroProps) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <motion.div
        className="w-full max-w-[700px] px-4 text-center flex flex-col items-center gap-6"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: eases.cubicOut }}
      >
        <motion.div
          className={
            tagBackground ? 'flex items-center justify-center size-16 shrink-0 rounded-md' : ''
          }
          style={tagBackground ? { backgroundColor: tagBackground } : undefined}
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.4, delay: 0.1, ease: eases.cubicOut }}
        >
          <span>{icon}</span>
        </motion.div>
        <div className="flex flex-col items-center gap-2">
          <div className="type-display-sm text-foreground">{title}</div>
          {description && <div className="type-body-lg text-foreground-subtle">{description}</div>}
        </div>
        {greeting && (
          <div className="type-body-lg text-foreground">
            <Markdown>{greeting}</Markdown>
          </div>
        )}
        {children}
      </motion.div>
    </div>
  );
}
